import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import PageWrapper from "../../components/layout/page/PageWrapper";
import { FormLayout } from "../../components/layout/forms/FormLayout";
import FormHeader from "../../components/layout/forms/header/FormHeader";
import Heading from "../../components/typography/Heading";
import Text from "../../components/typography/Text";

import TextField from "../../components/input/textfield/TextField";
import Button from "../../components/buttons/Button";
import StatusAlert from "../../components/alerts/StatusAlert";
import FeedbackStatus from "../../components/feedback/FeedbackStatus";

import { useSignIn } from "../../features/user/auth/hooks/useSignIn";

import "./LandingPage.css";

export default function LandingPage() {
  const { formData, status, handleChange, handleSubmit } = useSignIn();

  useEffect(() => {
    document.title = "Katch | Home";
  }, []);

  if (status.isUnverified) {
    return (
      <PageWrapper>
        <div className="home-page__verification">
          <FeedbackStatus
            title="Verify your email"
            description={
              <span>
                We've resent a link to{" "}
                <strong>{formData.email || "your email"}</strong>. Please check
                your inbox or spam folder.
              </span>
            }
            buttonText="Back to Sign in"
            onAction={() => {
              window.location.assign("/sign-in");
            }}
          />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="home-page">
        <main className="home-page__content">
          <section className="home-page__hero">
            <div className="home-page__hero-body">
              <Text as="span" className="home-page__eyebrow">
                DISCOVER KATCH
              </Text>

              <Heading as="h1" size="xl" className="home-page__hero-title">
                Stream Share <span>Connect</span>
              </Heading>

              <Text
                as="p"
                size="md"
                tone="secondary"
                className="home-page__hero-description"
              >
                Stream videos, share moments and connect with your community.
              </Text>
            </div>
          </section>

          <section className="home-page__auth">
            <FormLayout className="home-page__auth-card">
              <FormHeader
                title="Sign into Katch"
                description={
                  <>
                    Don't have an account?{" "}
                    <Link to="/sign-up" className="home-page__link">
                      Create account
                    </Link>
                  </>
                }
              />

              <form
                className="home-page__form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSubmit();
                }}
              >
                {status.message && <StatusAlert status={status} />}

                <div className="home-page__fields">
                  <TextField
                    name="email"
                    type="email"
                    label="Email address"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    required
                  />

                  <TextField
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    showPasswordOption
                    required
                  />
                </div>

                <Button
                  type="submit"
                  value={status.loading ? "Signing in..." : "Sign in"}
                  variant="primary"
                  disabled={status.loading}
                />
              </form>

              <div className="home-page__auth-footer">
                <Link to="/forgot-password" className="home-page__link">
                  Forgot password?
                </Link>
              </div>

              <div className="home-page__register">
                <Text as="p" tone="secondary" size="sm">
                  New to Katch?
                </Text>

                <Link to="/sign-up" className="home-page__register-link">
                  Create account
                </Link>
              </div>
            </FormLayout>

            <footer className="home-page__footer">
              <nav
                className="home-page__footer-nav"
                aria-label="Footer navigation"
              >
                <Link to="/about">About</Link>
                <Link to="/features">Features</Link>
                <Link to="/terms">Terms</Link>
                <Link to="/privacy">Privacy</Link>
                <Link to="/support">Support</Link>
              </nav>

              <Text
                as="p"
                size="xs"
                tone="secondary"
                className="home-page__copyright"
              >
                © 2026 KATCH.ONE
              </Text>
            </footer>
          </section>
        </main>
      </div>
    </PageWrapper>
  );
}
